/**
 * What the records table (`Led AI - Records Table.dc.html`) reads off a row.
 *
 * Pure functions only — no React — so the table, the list and the board all
 * read one answer for "what status is this, whose is it, is it late", and the
 * tests can pin those answers without rendering anything.
 *
 * ## Three status vocabularies, because the records have three
 *
 * A Procore punch item runs Procore's punch workflow (Draft → Initiated →
 * Work Required → Ready for Review → Ready to Close → Closed, with three
 * rejection branches). An observation runs Procore's observation workflow
 * (Initiated / Ready for review / Not accepted / Closed). An Autodesk issue
 * runs ACC's (Draft / Open / Pending / In review / Closed). The console's own
 * five-value `status` is a lossy fold of all three — it puts Initiated and
 * Not accepted in one bucket — so each vocabulary reads the record's NATIVE
 * value (`workflowStatus`, or the observation's stored `fieldValues.status`)
 * and only falls back to the folded one when that is all a record has.
 */

import type { ConsoleRow } from "../../api";
import {
  autodeskStatusForInvertedStatus,
  autodeskStatusIfRecognized,
  parseAutodeskIssueStatusChoice,
  type AutodeskIssueLocalKind,
} from "../../../../../backend/src/shared/autodeskIssueStatus";
import { parseDay } from "../../components/Calendar";
import { humanize } from "../../components/chrome";
import { RT } from "../../tokens";

export type RecordVocab = "punch" | "obs" | "issues";

/** The status glyph's shape (the design's `G` table). */
export type StatusGlyph = "dashed" | "ring" | "half" | "threeq" | "ringdot" | "check" | "x";

export interface StatusDef {
  key: string;
  label: string;
  color: string;
  glyph: StatusGlyph;
  /** Whose move it is, by role — never a stored holder string. */
  ball: "Creator" | "Punch item manager" | "Assignee" | "Final approver" | "Reviewer" | "";
  /** In the main flow (always shown as a list section, even when empty). */
  flow?: boolean;
}

const S = RT.status;

/** Procore's punch workflow, flow first, then its three rejection branches. */
export const PUNCH_STATUSES: StatusDef[] = [
  { key: "draft", label: "Draft", color: S.draft, glyph: "dashed", ball: "Creator", flow: true },
  { key: "initiated", label: "Initiated", color: S.initiated, glyph: "ring", ball: "Punch item manager", flow: true },
  { key: "work_required", label: "Work Required", color: S.working, glyph: "half", ball: "Assignee", flow: true },
  { key: "ready_for_review", label: "Ready for Review", color: S.working, glyph: "threeq", ball: "Punch item manager", flow: true },
  { key: "ready_to_close", label: "Ready to Close", color: S.readyToClose, glyph: "ringdot", ball: "Final approver", flow: true },
  { key: "closed", label: "Closed", color: S.closed, glyph: "check", ball: "", flow: true },
  { key: "work_not_accepted", label: "Work Not Accepted", color: S.rejected, glyph: "x", ball: "Assignee" },
  { key: "not_accepted", label: "Not Accepted", color: S.rejected, glyph: "x", ball: "Punch item manager" },
  { key: "in_dispute", label: "In Dispute", color: S.dispute, glyph: "x", ball: "Punch item manager" },
];

/** Procore's observation workflow — `OBSERVATION_STATUS_OPTIONS`. */
export const OBS_STATUSES: StatusDef[] = [
  { key: "initiated", label: "Initiated", color: S.initiated, glyph: "ring", ball: "Assignee" },
  { key: "ready_for_review", label: "Ready for review", color: S.working, glyph: "threeq", ball: "Creator" },
  { key: "not_accepted", label: "Not accepted", color: S.rejected, glyph: "x", ball: "Assignee" },
  { key: "closed", label: "Closed", color: S.closed, glyph: "check", ball: "" },
];

/** Autodesk Construction Cloud issues. */
export const ACC_STATUSES: StatusDef[] = [
  { key: "draft", label: "Draft", color: S.draft, glyph: "dashed", ball: "Creator" },
  { key: "open", label: "Open", color: S.initiated, glyph: "ring", ball: "Assignee" },
  { key: "pending", label: "Pending", color: S.working, glyph: "half", ball: "Assignee" },
  { key: "in_review", label: "In review", color: S.dispute, glyph: "threeq", ball: "Reviewer" },
  { key: "closed", label: "Closed", color: S.closed, glyph: "check", ball: "" },
];

export function statusesFor(vocab: RecordVocab): StatusDef[] {
  return vocab === "punch" ? PUNCH_STATUSES : vocab === "issues" ? ACC_STATUSES : OBS_STATUSES;
}

/** The board's columns that stand even when empty — somewhere to drop a card. */
export const BOARD_COLUMNS: Record<RecordVocab, readonly string[]> = {
  punch: ["work_required", "ready_for_review", "closed"],
  obs: ["initiated", "ready_for_review", "closed"],
  issues: ["open", "pending", "in_review", "closed"],
};

/**
 * What a board move asks of the person making it (the design's `txRule`).
 *
 *  - `send`    Initiated → Work Required on a punch: pick who it goes to;
 *              Procore's workflow emails them.
 *  - `done`    → Ready for review: say what was done (required), and an after
 *              photo if there is one.
 *  - `reject`  → a rejection branch: say what still needs fixing (required).
 *  - `confirm` → Ready to Close / Closed: an optional comment.
 *  - `reopen`  out of Closed: why (required).
 *  - `none`    anything else moves at once.
 */
export type MoveRule = "send" | "done" | "reject" | "confirm" | "reopen" | "none";

export function moveRule(vocab: RecordVocab, from: string, to: string): MoveRule {
  if (vocab === "issues") return "none";
  if (from === "closed") return "reopen";
  if (vocab === "punch") {
    if ((from === "initiated" || from === "draft") && to === "work_required") return "send";
    if (to === "ready_for_review") return "done";
    if (to === "work_not_accepted" || to === "not_accepted" || to === "in_dispute") return "reject";
    if (to === "ready_to_close" || to === "closed") return "confirm";
    return "none";
  }
  if (to === "ready_for_review") return "done";
  if (to === "not_accepted") return "reject";
  if (to === "closed") return "confirm";
  return "none";
}

/**
 * Whether a card can be dragged to another column. Only a record whose status
 * the console can write: a punch item, or an observation stored as a Procore
 * daily-log row. Autodesk records keep ACC's workflow, which the console does
 * not drive.
 */
export function canMove(row: ConsoleRow, vocab: RecordVocab): boolean {
  if (vocab === "issues") return accKindOf(row) !== null;
  if (row.syncSource === "autodesk") return false;
  if (vocab === "punch") return row.kind === "punch";
  if (vocab === "obs") return row.kind === "log";
  return false;
}

/**
 * Which local record an Autodesk issue is, for ACC's status mapping — the
 * phone's `autodeskStatusKind`. A narrative note (`kind: "observation"`) has
 * no status to write, so it is not one.
 */
export function accKindOf(row: ConsoleRow): AutodeskIssueLocalKind | null {
  if (row.kind === "task" || row.kind === "punch") return row.kind;
  if (row.kind === "log" && row.recordType === "observation") return "observation";
  return null;
}

/** The edit patch that moves a record to `to`, with what the move collected —
 *  in the shape `updateReviewedItem` takes for that kind. The comment rides
 *  as `statusNote`, which lands on the edit's history event. */
export function movePatch(
  vocab: RecordVocab,
  to: StatusDef,
  fill: { assignee?: { remoteId?: string; name: string }; dueDate?: string; note?: string } = {},
  row?: Pick<ConsoleRow, "kind" | "recordType">,
): Record<string, unknown> {
  const extra = {
    ...(fill.assignee ? { selectedAssigneeRemoteId: fill.assignee.remoteId ?? null, selectedAssigneeName: fill.assignee.name } : {}),
    ...(fill.note?.trim() ? { statusNote: fill.note.trim() } : {}),
  };
  if (vocab === "issues") {
    // ACC's status (Draft / Open / Pending / In review / Closed), spelled in
    // the local record's own vocabulary — exactly what the phone's editor
    // writes (`parseAutodeskIssueStatusChoice`), so the backend pushes the
    // same ACC status either way.
    const kind = row ? accKindOf(row as ConsoleRow) : null;
    if (!kind) throw new Error("This record has no Autodesk status to move.");
    const local = parseAutodeskIssueStatusChoice(to.key, kind);
    return kind === "observation" ? { fieldValues: { status: local }, ...extra } : { status: local, ...extra };
  }
  if (vocab === "obs") {
    return { fieldValues: { status: to.label, ...(fill.dueDate ? { dueDate: fill.dueDate } : {}) }, ...extra };
  }
  return { status: to.key, ...(fill.dueDate ? { dueDate: fill.dueDate } : {}), ...extra };
}

/** Who moves it next, and what they do — the status tooltip. */
const NEXT: Record<RecordVocab, Record<string, [string, string]>> = {
  punch: {
    draft: ["Creator", "Submit the item to the punch item manager"],
    initiated: ["Punch item manager", "Send to the assignee — Procore emails them"],
    work_required: ["Assignee", "Do the work and mark it Ready for Review"],
    work_not_accepted: ["Assignee", "Fix the rejected work and resubmit"],
    ready_for_review: ["Punch item manager", "Inspect the work — accept or reject"],
    ready_to_close: ["Final approver", "Final sign-off closes the item"],
    not_accepted: ["Punch item manager", "Final approver rejected it — needs more work"],
    in_dispute: ["Punch item manager", "Resolve the dispute before sending"],
    closed: ["No one", "Done — nothing further"],
  },
  obs: {
    initiated: ["Assignee", "Address it and mark it Ready for review"],
    ready_for_review: ["Creator", "Review the fix — close it or send it back"],
    not_accepted: ["Assignee", "The fix was not accepted — address it again"],
    closed: ["No one", "Done — nothing further"],
  },
  issues: {
    draft: ["Creator", "Publish the issue so the assignee sees it"],
    open: ["Assignee", "Do the work and mark it Pending"],
    pending: ["Assignee", "Work done — waiting on review"],
    in_review: ["Reviewer", "Inspect and close, or reopen"],
    closed: ["No one", "Done — nothing further"],
  },
};

/** `"Ready for review"` → `"ready_for_review"`. */
export function statusKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function fieldText(row: ConsoleRow, key: string): string | undefined {
  const raw = row.fieldValues?.[key];
  if (raw === undefined || raw === null) return undefined;
  const text = String(raw).trim();
  return text ? text : undefined;
}

export interface ObservationFacts {
  /** The SOURCE status key, from the observation's own vocabulary. */
  status: string;
  /** The observation's own first field — "Material Deficiency", "Safety". */
  type: string | undefined;
  /** Procore's number for it, once it has synced. Absent before that. */
  number: string | undefined;
  severity: "low" | "medium" | "high" | undefined;
  assignee: string | undefined;
  location: string | undefined;
  /** Local calendar day, matching the detail view and date picker. */
  due: Date | undefined;
}

/**
 * One observation's properties, whichever record it is stored as.
 *
 * An observation on a Procore-format walk is a daily-log row whose
 * properties live in `fieldValues` under the mobile schema's `observations`
 * definition; the projection deliberately does not lift them into the
 * top-level slots (other kinds' fields, other vocabularies). So the schema
 * wins where it has an answer and the row's own columns fill the rest —
 * which also makes this right for a punch item or a task.
 */
export function observationFacts(row: ConsoleRow): ObservationFacts {
  const rawPriority = fieldText(row, "priority")?.toLowerCase();
  const severity =
    rawPriority === "high" || rawPriority === "medium" || rawPriority === "low"
      ? rawPriority
      : row.severity;
  const rawDue = fieldText(row, "dueDate") ?? row.dueDate;
  // Due dates are calendar days, including when serialized as UTC midnight.
  // Parsing them as instants moves the list one day back west of UTC.
  const due = rawDue ? parseDay(rawDue) : null;
  return {
    status: statusKey(fieldText(row, "status") ?? row.workflowStatus ?? row.status),
    type: fieldText(row, "type"),
    // Procore's NUMBER, never its id — an id under `#` is the right shape to
    // be mistaken for the thing a super reads out on site.
    number: row.procoreSync?.remoteNumber,
    severity,
    assignee: fieldText(row, "assignee") ?? row.assigneeName,
    location: row.location?.name ?? fieldText(row, "location"),
    due: due ?? undefined,
  };
}

/** The status a row is in, in its vocabulary's own words. */
export function statusOf(row: ConsoleRow, vocab: RecordVocab): StatusDef {
  const set = statusesFor(vocab);
  const find = (key: string) => set.find((def) => def.key === key);
  if (vocab === "punch") {
    // The projection fills `workflowStatus` with the record's plain `status`
    // when Procore never gave it one, so the console's own words are aliases
    // here, not unknown states.
    const FOLDED: Record<string, string> = { open: "initiated", in_progress: "work_required", completed: "closed", logged: "closed" };
    const native = statusKey(row.workflowStatus ?? "");
    const known = find(native) ?? find(FOLDED[native] ?? "");
    if (known) return known;
    if (native) return unknownStatus(native);
    return find(FOLDED[row.status] ?? row.status) ?? unknownStatus(row.status);
  }
  if (vocab === "obs") {
    const key = observationFacts(row).status;
    const mapped =
      key === "open" || key === "in_progress" ? "initiated"
        : key === "ready" ? "ready_for_review"
          : key === "logged" || key === "voided" || key === "completed" ? "closed"
            : key;
    return find(mapped) ?? unknownStatus(key);
  }
  // The record's own status, read as ACC reads it: an observation keeps it
  // in `fieldValues.status`, a task or punch item in its status, both in the
  // local spelling ACC's status was written in (`not_accepted` is Pending).
  // The console's folded `status` cannot tell Open from Pending.
  const native = fieldText(row, "status") ?? row.workflowStatus;
  const remote = native ? autodeskStatusIfRecognized(statusKey(native)) ?? autodeskStatusForInvertedStatus(statusKey(native)) : undefined;
  if (remote) return find(remote)!;
  if (statusKey(row.workflowStatus ?? "") === "draft") return find("draft")!;
  const folded = { open: "open", in_progress: "pending", ready_for_review: "in_review", closed: "closed", logged: "closed" }[row.status];
  return find(folded ?? "") ?? unknownStatus(row.status);
}

/** A status nobody predicted is a thing to SEE, named after itself — never
 *  swept into Closed. */
function unknownStatus(raw: string): StatusDef {
  const key = statusKey(raw || "unknown");
  return { key, label: humanize(key), color: S.draft, glyph: "ring", ball: "" };
}

export function isClosed(status: StatusDef): boolean {
  return status.key === "closed";
}

export type Priority = "high" | "medium" | "low";
export const PRIORITY_LABEL: Record<Priority, string> = { high: "High", medium: "Medium", low: "Low" };
export const PRIORITY_RANK: Record<Priority, number> = { low: 1, medium: 2, high: 3 };

export type SyncState = "synced" | "pending" | "failed";

/** One row, read once for every column, cell and filter that shows it. */
export interface RecordFacts {
  row: ConsoleRow;
  /** Procore's number for punch and observations; none for an ACC issue. */
  number: string | undefined;
  status: StatusDef;
  priority: Priority | undefined;
  assignee: string | undefined;
  /** Only a company somebody actually knows — never the assignee's own name
   *  standing in for one (`companySource: "assignee-name"`). */
  company: string | undefined;
  location: string | undefined;
  /** The last tier of the location path — `Unit 216`. */
  locShort: string | undefined;
  /** Punch: trade. Observation and issue: type. */
  category: string | undefined;
  due: Date | undefined;
  overdue: boolean;
  created: Date;
  photos: number;
  sync: SyncState | undefined;
  syncedAt: Date | undefined;
  /** The tooltip over the status: who moves it next, and what they do. */
  tipWho: string;
  tipWhat: string;
}

export function syncOf(row: ConsoleRow): SyncState | undefined {
  // An Autodesk record's Procore state is a stray stamp, not a sync.
  if (row.syncSource === "autodesk") return undefined;
  const status = row.procoreSync?.status;
  if (!status || status === "idle" || status === "skipped") return undefined;
  if (status === "synced" || status === "partial") return "synced";
  if (status === "failed" || status === "conflict" || status === "blocked") return "failed";
  return "pending";
}

export function recordFacts(row: ConsoleRow, vocab: RecordVocab, today: Date = new Date()): RecordFacts {
  const facts = observationFacts(row);
  const status = statusOf(row, vocab);
  const location = facts.location;
  const company =
    row.assigneeCompany && row.assigneeCompanySource !== "assignee-name" ? row.assigneeCompany : undefined;
  const assignee = vocab === "punch" ? row.assigneeName : facts.assignee;
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const overdue = !!facts.due && facts.due.getTime() < midnight.getTime() && !isClosed(status);
  const next = NEXT[vocab][status.key] ?? ["", ""];
  const syncedAt = row.procoreSync?.syncedAt ? new Date(row.procoreSync.syncedAt) : undefined;
  return {
    row,
    number: vocab === "punch" ? row.punchNumber : vocab === "obs" ? facts.number : undefined,
    status,
    priority: facts.severity,
    assignee,
    company,
    location,
    locShort: location?.split("›").map((part) => part.trim()).filter(Boolean).at(-1),
    category: vocab === "punch" ? row.trade : facts.type,
    due: facts.due,
    overdue,
    created: new Date(row.createdAt),
    photos: row.photoCount ?? 0,
    sync: syncOf(row),
    syncedAt: syncedAt && !Number.isNaN(syncedAt.getTime()) ? syncedAt : undefined,
    // The role from the workflow; the person only when the role IS the
    // assignee and the record names one.
    tipWho: status.ball === "Assignee" && assignee ? assignee : next[0],
    tipWhat: next[1],
  };
}

/** Deterministic pick from a palette, so a name keeps its colour everywhere. */
function pick<T>(palette: readonly T[], key: string): T {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length]!;
}

export function avatarTone(name: string): { bg: string; fg: string } {
  const [bg, fg] = pick(RT.avatars, name);
  return { bg, fg };
}

export function companyTone(name: string): { fill: string; ink: string; text: string } {
  const [fill, ink] = pick(RT.companies, name);
  const neutral = fill === RT.companies[RT.companies.length - 1]![0];
  return { fill, ink, text: neutral ? RT.companyMuted : "inherit" };
}

/** `Bright Electrical` → `BE`. */
export function companyInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((word) => word[0]).join("").slice(0, 2).toUpperCase();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `Oct 12` — the list's and board's short date. */
export function shortDay(date: Date): string {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/** `9:13a` — the design's clock. */
export function clock(date: Date): string {
  const hours = date.getHours() % 12 || 12;
  return `${hours}:${String(date.getMinutes()).padStart(2, "0")}${date.getHours() < 12 ? "a" : "p"}`;
}

/** `Oct 12, 2026 · 9:13a` — the Created cell. */
export function createdStamp(date: Date): string {
  if (Number.isNaN(date.getTime())) return "—";
  return `${shortDay(date)}, ${date.getFullYear()} · ${clock(date)}`;
}
